import Link from 'next/link';
import * as Typography from '@/components/shadcn/typography';
import {
  personalInformationItems,
  privacyPracticesItems,
  privacyRightsItems,
} from '@/utils/privacy';
import { ExternalUrls } from '@/utils/url.ts';

const PrivacyPolicy = () => {
  return (
    <div className="flex flex-col gap-12">
      <section>
        <div className="pb-4 text-neutral-400">
          <p>Last Updated: June 25, 2024</p>
        </div>
        <Typography.H4>Privacy Policy</Typography.H4>
        <Typography.P>
          Hivemapper is committed to protecting your privacy when processing
          your Personal Information. This Privacy Policy sets out the basis on
          which Personal Information will be collected, stored and used by us
          when you use our website{' '}
          <Link
            className="cursor-pointer underline"
            href={ExternalUrls.HivemapperWeb}>
            https://hivemapper.com/
          </Link>
          , mobile application (“mobile app”), products or otherwise interact
          with us. “Personal Information” means any information that we process
          about you that relates to you or that can be used to identify you
          directly or indirectly, for example your name, email address, payment
          details, product order details or any user profile related content.
        </Typography.P>
        <Typography.P>
          References to “Hivemapper”, “we”, “us” or “our” in this Privacy Policy
          are to, Hivemapper Inc, a corporation registered in the State of
          Delaware at 221 N. Broad Street, Suite A, in the City of Middleton, DE
          19709 County of New Castle, and related parties. We are the controller
          of your Personal Information described in this Privacy Policy (this
          simply means we are responsible for your Personal Information). Please
          only use our website or otherwise provide information to Hivemapper,
          if you understand the terms of this Privacy Policy and agree with our
          data processing. If you do not agree with the manner in which we
          process your Personal Information, please do not visit or use our
          website and please do not use our products.
        </Typography.P>
      </section>
      <section>
        <Typography.H4>Our Privacy Practices: The Big Picture</Typography.H4>
        <ul className="my-6 ml-6 list-disc [&>li]:mt-2">
          {privacyPracticesItems.map((item, index) => (
            <li
              dangerouslySetInnerHTML={{ __html: item }}
              key={index.toString()}></li>
          ))}
        </ul>
        <Typography.H4>Creating the Map</Typography.H4>
        <Typography.P>
          As explained above, Hivemapper makes maps using blurred image data
          provided by contributors using a dashcam while they drive around. Our
          mapping activities are focused on collecting road imagery and
          garnering insights about objects on the public right of way (e.g.,
          speed signs, construction zones, locations of EV chargers, etc.).
        </Typography.P>
        <Typography.P>
          Contributors agree to share image data from their dashcams and can
          disable sharing with Hivemapper at any time by uninstalling or logging
          out of the Hivemapper app. To protect contributors, we do not collect
          personal identifiers (e.g., email addresses, phone numbers or social
          media profiles) to set up Hivemapper user accounts, but instead
          ascribe contributors a randomly-generated three-word username so that
          we can track and reward contributorsy&apos; mapping activities.
          Further, we allow contributors to create personal privacy zones around
          locations such as their home or workplace, where image data will never
          be submitted. Contributors can stop collecting images at any time by
          dismounting their dashcam. Viewers of our maps cannot link any image
          to a specific contributor and username, or a specific contributor or
          username to a location or region they map. Further, we allow
          contributors to create personal privacy zones around locations such as
          their home or workplace, where image data will never be submitted.
          Contributors can stop collecting images at any time by dismounting
          their dashcam.
        </Typography.P>
        <Typography.P>
          While adding to the map, contributors may inadvertently collect image
          data of road users (bystanders) within the view of the dashcam (i.e.,
          faces, bodies, vehicles and license plates.). However, full bodies
          (including faces) and vehicles (including license plates) are
          automatically blurred on each contributorsy&apos; dashcam, to ensure
          image data shared with us does not include Personal Information of
          bystanders. Unblurred image data is not shared with the Hivemapper
          mobile application and Hivemapper has no access to unblurred image
          data. We will not sell or make available to the public maps containing
          unblurred images.
        </Typography.P>
        <Typography.P>
          If somebody&apos;s business or home appears on the map, that
          individual can request that images taken of their home or business be
          blurred by sending a message to{' '}
          <Link
            className="cursor-pointer underline"
            href="mailto:privacy@hivemapper.com">
            privacy@hivemapper.com
          </Link>{' '}
          or{' '}
          <Link
            className="cursor-pointer underline"
            href="/privacy/redaction-request">
            filling out an online form
          </Link>{' '}
          in our privacy portal. Individuals can also request for map images
          they consider sensitive or their Personal Information to be further
          blurred or deleted through the same channels.
        </Typography.P>
      </section>
      <section>
        <Typography.H4>How We Use Your Personal Information</Typography.H4>
        <Typography.P>
          The table below sets out clearly how we collect and use your Personal
          Information. Under the laws of certain countries, including the United
          Kingdom (UK) and countries in the European Economic Area (“EEA”), we
          can only use your Personal Information where we have a lawful reason
          (also called a “lawful basis”) and this can be either “to carry out a
          contract with you or to take steps on your instruction prior to
          entering a contract with you”; “where we have a legal obligation”;
          where you have given us your consent”; or “where it is necessary for
          our legitimate interests” (this means that we have commercial
          interests in using your Personal Information that is not unfair to
          you) – we have included each lawful basis as a subheading in the table
          below for information.
        </Typography.P>
        <Typography.P>
          Please note that if you choose not to provide Personal Information
          requested by us, or refuse our use of your Personal Information, we
          may not be able to provide you with the services you have requested
          where this Personal Information is necessary or these services may be
          delayed.
        </Typography.P>
      </section>
      <section>
        <div className="mb-4">
          <Typography.H4>Information You Give Us</Typography.H4>
        </div>
        <div className="grid grid-cols-2 gap-x-10 gap-y-4">
          <div>
            <Typography.H4>Personal Information We Collect</Typography.H4>
          </div>
          <div>
            <Typography.H4>
              How We Use Your Personal Information and Our Lawful Basis
            </Typography.H4>
          </div>
          <div>
            <Typography.H4>Hivemapper User Account</Typography.H4>
            <Typography.P>
              You give us Personal Information about you when you place an order
              on our website or any of our online stores. This is generally
              information including the contents of your shopping cart, your
              name, email address, telephone number, shipping address, billing
              address, delivery preferences and payment details (e.g.
              credit/debit card/PayPal)
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Performance of Contract</Typography.H4>
            <Typography.P>
              To register and maintain your account and public profile in our
              database, (including providing you with related user support to
              help you resolve any issues you have with your account and to
              attribute contributor activity to you). Since we dony&apos;t
              collect personal identifiers when you sign up for a user account
              (e.g, name, email address or social network), youy&apos;re not
              personally identifiable from your username or public profile,
              unless you provide additional identifiable information.
            </Typography.P>
            <Typography.P>
              We use your Personal Information to send you important information
              you need to know relating to how our services work, including
              information on changes to our terms, conditions and policies.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Purchases/Orders</Typography.H4>
            <Typography.P>
              You give us Personal Information about you when you place an order
              on our website or any of our online stores. This is generally
              information including the contents of your shopping cart, your
              name, email address, telephone number, shipping address, billing
              address, delivery preferences and payment details (e.g.
              credit/debit card/PayPal)
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Performance of Contract</Typography.H4>
            <Typography.P>
              To complete and fulfill your purchases on our website and online
              stores (including processing your payments, sending you an order
              confirmation, sending you updates on your order, having your order
              delivered to you and related customer service).
            </Typography.P>
            <Typography.P>
              To complete and fulfill your purchases on our website and online
              stores (including processing your payments, sending you an order
              confirmation, sending you updates on your order, having your order
              delivered to you and related customer service).
            </Typography.P>
            <Typography.P>
              To send you important information you need to know relating to how
              our services work, including information on changes to our terms,
              conditions and policies.
            </Typography.P>
            <div className="mt-4">
              <Typography.H4>Comply with a Legal Obligation</Typography.H4>
            </div>
            <Typography.P>
              To comply with any relevant consumer laws in relation to purchases
              you make, including regarding giving you certain information and
              issuing replacements, credits or refunds (if applicable).
            </Typography.P>
            <Typography.P>
              We do not track the serial number of the dashcam that is shipped
              to fulfill an order, which means we cannot tie this personal
              information to any specific username, dashcam or map contribution.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Image Data (Contributors)</Typography.H4>
            <Typography.P>
              You give us Personal Information about you when you share blurred
              image data with us through the Hivemapper mobile application as a
              contributor. This includes the blurred image file, total road
              driven in kilometers, the region where image data is collected and
              the precise location of the mapping.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Performance of Contract</Typography.H4>
            <Typography.P>
              To process and assess, blurred image data you submit as a
              contributor for inclusion on the map where you are a dashcam
              contributor, and feedback you provide as a quality reviewer
              contributor or Map AI Trainer.
            </Typography.P>
            <Typography.P>
              To determine contributor activity that should be attributed to you
              and the Honey Tokens you have earned.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Marketing Communications and Updates</Typography.H4>
            <Typography.P>
              You give us Personal Information about you when you subscribe to
              receive marketing communications and updates from us, or we are
              otherwise legally allowed to send you such marketing
              communications. This is generally information including your name,
              email address and your marketing preferences (i.e., the marketing
              and updates you want to receive and how you want to receive them).
            </Typography.P>
          </div>
          <div>
            <Typography.H4>With your Consent</Typography.H4>
            <Typography.P>
              To send you marketing communications (and to manage our marketing
              processes) where you have provided your consent to receive these
              communications. We will only do this in line with your marketing
              preferences. You can opt-out of receiving these communications at
              any time by selecting the unsubscribe link in any email we send
              you, or by contacting us.
            </Typography.P>
            <div className="mt-4">
              <Typography.H4>Legitimate Interests</Typography.H4>
            </div>
            <Typography.P>
              To send you marketing communications (and to manage our marketing
              processes) where we are legally allowed to do so without having
              received your consent. For example, in some countries we are
              allowed to send marketing communications to customers who have
              made purchases without receiving prior consent. You can opt-out of
              receiving these communications at any time by selecting the
              unsubscribe link in any email we send you, or by contacting us.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Surveys</Typography.H4>
            <Typography.P>
              You give us Personal Information about you when you participate in
              and complete any of our surveys. This is generally information
              including your name, contact information, and your survey
              responses.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              To register you for our surveys and to analyze the responses you
              have provided to our survey questions.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Reviews and Testimonials</Typography.H4>
            <Typography.P>
              You give us Personal Information about you when you write a
              review/testimonial about us or any of our products. This is
              generally information including your name, email address, content
              of your review/testimonial and any images or videos you provide
              with your review/testimonial.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>With your Consent</Typography.H4>
            <Typography.P>
              To publish your reviews and testimonials on our website and/or
              marketing materials along with your name or other identifying
              information about you.
            </Typography.P>
            <div className="mt-4">
              <Typography.H4>Legitimate Interests</Typography.H4>
            </div>
            <Typography.P>
              To publish your reviews and testimonials on our website and/or
              marketing materials without including identifying information
              about you.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>
              Suppliers/Partners and Representatives
            </Typography.H4>
            <Typography.P>
              You give us Personal Information about you when you enter into a
              supplier/partnership agreement or relationship with us or are a
              representative of a supplier/partner. This is generally
              information including your name, email, phone number.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              We use your Personal Information to contact you and to manage our
              relationship with you and/or your company where you are a
              supplier/partner or the representative of a supplier/partner with
              whom we have a business relationship.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Contacting Us</Typography.H4>
            <Typography.P>
              You give us Personal Information about you when you contact us or
              otherwise interact with us, including via the contact us web forms
              on our website, phone, email, post or social media. This is
              generally information which may include your name, contact
              preference (email/phone number) and what you have contacted us
              about.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              To respond to your inquiries and fulfill your requests, for
              example, when you send us questions, suggestions, compliments, or
              complaints, or when you request information about our dashcam
              product, contributor operations or other offerings. We may also
              take this information into account when improving our platform,
              products, and services.
            </Typography.P>
          </div>
        </div>
      </section>
      <section>
        <div className="mb-4">
          <Typography.H4>Information You Give Us</Typography.H4>
        </div>
        <div className="grid grid-cols-2 gap-x-10 gap-y-4">
          <Typography.H4>Personal Information We Collect</Typography.H4>
          <Typography.H4>
            How We Use Your Personal Information and Our Lawful Basis
          </Typography.H4>
          <div>
            <Typography.H4>Visiting Our Website</Typography.H4>
            <Typography.P>
              We collect Personal Information about you automatically when you
              visit and interact with our website. Our servers keep an activity
              log unique to you that collects certain administrative and traffic
              information including your device details, device location, source
              IP address, time of access, date of access, web page(s) visited,
              language use, software crash reports and type of browser used.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              We use your Personal Information to make sure you are able to use
              our website, to monitor how our website is being used, to help us
              discover and fix any problems with our website and to determine
              what country you are in when you use our website.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Using Our Mobile App</Typography.H4>
            <Typography.P>
              We collect Personal Information about you automatically when you
              download and use the Hivemapper mobile app. This is generally
              information that includes your geographic location and the make
              and model of your mobile device. This information is not shared.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Performance of Contract</Typography.H4>
            <Typography.P>
              To make sure you are able to use our mobile app.
            </Typography.P>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              To monitor how our mobile app is being used, and to help us
              discover and fix any problems with our mobile app.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Cookies (and Similar Technologies)</Typography.H4>
            <Typography.P>
              We collect Personal Information about you by automated means when
              you visit and use any of our websites or mobile apps through
              cookies and similar technologies (e.g. web beacons, pixel tags,
              etc.) placed on your device. Cookies and similar technologies are
              simple pieces of computer code which are designed to collect
              information from the device you use to access our website.
            </Typography.P>
            <Typography.P>
              The information collected may include a user ID assigned to you,
              your IP address, the content you have interacted with, duration of
              each visit and error messages you encounter. Our website may use
              “cookies“ or similar technologies to enhance User experience. A
              cookie is a small file placed on your device or computer. It is
              possible to refuse to accept cookies by activating the appropriate
              setting on your browser or mobile device. However, if you select
              this setting, you may be unable to access certain parts of our
              website. We use these technologies to collect and store
              information about you when you interact with services or products
              that we offer.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Consent</Typography.H4>
            <Typography.P>
              To carry out web analytics on our website (e.g., to monitor the
              number of your visits to our website and the actions you take on
              our website for analytical purposes).
            </Typography.P>
            <Typography.P>
              To ensure our services work as intended on your device and can
              provide the content or perform the actions you request while using
              our services.
            </Typography.P>
            <Typography.P>
              To enhance the operation of our website, improve our marketing and
              promotional efforts, statistically analyze website use, improve
              our product and service offerings, and customize our
              websitey&apos;s content, layout, and services.
            </Typography.P>
            <div className="mt-4">
              <Typography.H4>Legitimate Interests</Typography.H4>
            </div>
            <Typography.P>
              We use your Personal Information as strictly necessary to ensure
              you can use our website, to ensure security of our website and to
              record your cookie consent preferences.
            </Typography.P>
          </div>
        </div>
      </section>
      <section>
        <div className="mb-4">
          <Typography.H4>Information Provided by Third Parties</Typography.H4>
        </div>
        <div className="grid grid-cols-2 gap-x-10 gap-y-4">
          <Typography.H4>Personal Information We Collect</Typography.H4>
          <Typography.H4>
            How We Use Your Personal Information and Our Lawful Basis
          </Typography.H4>
          <div>
            <Typography.H4>
              Personal Information provided by your affiliated company
            </Typography.H4>
            <Typography.P>
              Where you are an employee or representative of any of the
              companyy&apos;s we have a relationship with (e.g., a customer,
              supplier, or partner of Hivemapper), we may be provided with your
              Personal Information by your affiliated company during this
              relationship. This is generally information including your name,
              contact details, job title and company.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              We use your Personal Information strictly in the context of the
              business purposes with your affiliated company.
            </Typography.P>
          </div>
        </div>
      </section>
      <section>
        <div className="mb-4">
          <Typography.H4>Internal Business Purposes</Typography.H4>
        </div>
        <div className="grid grid-cols-2 gap-x-10 gap-y-4">
          <Typography.H4>Personal Information We Collect</Typography.H4>
          <Typography.H4>
            How We Use Your Personal Information and Our Lawful Basis
          </Typography.H4>
          <div>
            <Typography.H4>Fraud and Security Monitoring</Typography.H4>
            <Typography.P>
              Our fraud and security monitoring processes may apply to any of
              the above Personal Information we collect about you.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              We use your Personal Information to carry out fraud and security
              monitoring within our business (e.g., to detect and prevent
              cyberattacks or attempts to commit identity theft.).
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Business Administration</Typography.H4>
            <Typography.P>
              Our processes for ensuring the proper day to day running of our
              business may apply to any of the above Personal Information we
              collect about you.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              We use your Personal Information as necessary to operate our
              business efficiently.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>
              Legal Claims and Compliance with Legal Obligations
            </Typography.H4>
            <Typography.P>
              Our processes for complying with our legal obligations and
              protecting our rights under the law may apply to any of the
              Personal Information we collect about you listed in this Privacy
              Policy.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Legitimate Interests</Typography.H4>
            <Typography.P>
              We use your Personal Information to effectively defend or bring
              claims to protect our legal rights and interests, to respond to
              investigations from regulators or law enforcement, and to audit
              our internal processes for compliance with legal and contractual
              requirements.
            </Typography.P>
            <div className="mt-4">
              <Typography.H4>Legal Obligation</Typography.H4>
            </div>
            <Typography.P>
              We use your Personal Information to comply with applicable laws,
              lawful requests, and legal process, such as to respond to
              subpoenas or requests from government authorities and audit our
              internal processes for compliance with legal requirements.
            </Typography.P>
          </div>
        </div>
      </section>
      <section>
        <Typography.H4>How We Share Your Personal Information</Typography.H4>
        <Typography.P>
          We share your Personal Information for the following purposes and in
          line with this Privacy Policy:
        </Typography.P>
        <Typography.List list={personalInformationItems} />
      </section>
      <section>
        <Typography.H4>International Transfers</Typography.H4>
        <Typography.P>
          Where you provide us with any information as a contributor or
          otherwise, please note that Hivemapper is based in the United States
          (US) and we use service providers in the US, which means Personal
          Information you provide to us will be processed and stored in the US.
          Please note that the US may have data protection laws less stringent
          than or otherwise different from the laws in effect in your country.
        </Typography.P>
      </section>
      <section>
        <Typography.H4>Personal Information Retention</Typography.H4>
        <Typography.P>
          We generally retain your Personal Information only for as long as
          necessary to fulfill the purposes outlined in this Privacy Policy.
          This will generally be for the duration of time you interact with us,
          however, there may be situations where we retain your Personal
          Information for a longer or shorter period. When determining the
          relevant retention periods for your Personal Information, we take into
          account the following factors: (a) any permissions you give us with
          regard to your Personal Information; (b) our contractual obligations
          and rights in relation to the Personal Information involved; (c) our
          legal obligation(s) under relevant laws to retain data for a certain
          period of time; (d) our legitimate business and commercial interests;
          (e) whether retention is advisable in light of our legal position
          (such as with regard to applicable statute of limitations,
          investigations, litigation, and other potential and actual disputes);
          and (f) any guidelines issued by relevant data protection authorities.
        </Typography.P>
      </section>
      <section>
        <Typography.H4>Rights of UK and EEA Residents</Typography.H4>
        <Typography.P>
          Depending on where you live and the circumstances, you have specific
          rights concerning your Personal Information and can exercise these
          rights by contacting us.
        </Typography.P>
        <Typography.P>
          You have the right to object to us processing your Personal
          Information where we rely on “legitimate interest” (see the “How We
          Use Your Personal Information” section above) as a lawful basis for
          processing your Personal Information or where we are processing your
          Personal Information for any direct marketing purposes (e.g. to send
          you newsletters or promotional emails).
        </Typography.P>
        <Typography.P>You also have the right to:</Typography.P>
        <ul className="my-6 ml-6 list-disc [&>li]:mt-2">
          {privacyRightsItems.map((item, index) => (
            <li
              dangerouslySetInnerHTML={{ __html: item }}
              key={index.toString()}></li>
          ))}
        </ul>
      </section>
      <section>
        <Typography.H4>How We Protect Your Information</Typography.H4>
        <Typography.P>
          We adopt data collection, storage and processing technical and
          organizational measures to secure and protect against unauthorized
          access, alteration, disclosure or destruction of your personal
          information, username, password, transaction information and data
          stored on our services. While we use security measures to protect
          sensitive and personal information transmitted online, please be aware
          that no security measures are perfect or impenetrable.
        </Typography.P>
      </section>
      <section>
        <Typography.H4>Childreny&apos;s Information</Typography.H4>
        <Typography.P>
          Our Services are not directed to children under the age of 13, and our
          Terms and Conditions prohibit children under 13 from using our
          Services. We do not knowingly collect any information from children
          under 13.
        </Typography.P>
      </section>
      <section>
        <Typography.H4>Changes to this Privacy Policy</Typography.H4>
        <Typography.P>
          We may periodically make changes to this Privacy Policy. We will
          notify you of any significant changes where we have a relationship
          with you and otherwise post updated versions here. We recommend that
          you revisit this Privacy Policy regularly.
        </Typography.P>
      </section>
      <section>
        <Typography.H4>How to Contact Us</Typography.H4>
        <Typography.P>
          If you wish to exercise any data subject rights or have any questions
          about this Privacy Policy or our information practices, please feel
          free to contact us at{' '}
          <Link
            className="cursor-pointer underline"
            href="mailto:privacy@hivemapper.com">
            privacy@hivemapper.com.
          </Link>
        </Typography.P>
        <Typography.P>
          Since we are not currently located in Europe, the law requires us to
          have a data protection representative in place who you can also
          contact at any time. Please see details of our representative below:
        </Typography.P>
        <Typography.P>
          UK Representative: VeraSafe has been appointed as Hivemapper&apos;s
          representative in the United Kingdom for data protection matters,
          pursuant to Article 27 of the United Kingdom General Data Protection
          Regulation. If you are located within the United Kingdom, VeraSafe can
          be contacted only on matters related to the processing of personal
          data. To make such an inquiry, please contact VeraSafe using this
          contact form:{' '}
          <Link
            className="cursor-pointer underline"
            target="_blank"
            href={ExternalUrls.VeraSafeWebsite}>
            https://verasafe.com/public-resources/contact-data-protection-representative
          </Link>{' '}
          or via telephone at: +44 (20) 4532 2003. Alternatively, VeraSafe can
          be contacted at: VeraSafe United Kingdom Ltd., 37 Albert Embankment,
          London SE1 7TL, United Kingdom
        </Typography.P>
        <Typography.P>
          EU Representative: VeraSafe has been appointed as Hivemapper&apos;s
          representative in the European Union for data protection matters,
          pursuant to Article 27 of the General Data Protection Regulation of
          the European Union. If you are in the European Economic Area, VeraSafe
          can be contacted only on matters related to the processing of personal
          data. To make such an inquiry, please contact VeraSafe using this
          contact form:{' '}
          <Link
            className="cursor-pointer underline"
            target="_blank"
            href={ExternalUrls.VeraSafeWebsite}>
            https://verasafe.com/public-resources/contact-data-protection-representative
          </Link>{' '}
          or via telephone at: +420 228 881 031. Alternatively, VeraSafe can be
          contacted at: VeraSafe Ireland Ltd., Unit 3D North Point House, North
          Point Business Park, New Mallow Road, Cork T23AT2P, Ireland
        </Typography.P>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
